import React from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal, X } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SEARCH_SORT_OPTIONS, SEARCH_TYPES } from '@constants/search';
import type { SearchFacets } from '@hooks/useSearchFacets';
import FilterCheckboxGroup from './FilterCheckboxGroup';

export interface SearchFiltersType {
  categories: string[];
  fileTypes: string[];
  dateRange: { start: Date | null; end: Date | null };
  tags: string[];
  uploadedBy: string[];
  searchType: 'basic' | 'semantic' | 'exact';
  sortBy: 'relevance' | 'date' | 'title' | 'size';
  sortOrder: 'asc' | 'desc';
}

export const DEFAULT_SEARCH_FILTERS: SearchFiltersType = {
  categories: [],
  fileTypes: [],
  dateRange: { start: null, end: null },
  tags: [],
  uploadedBy: [],
  searchType: 'basic',
  sortBy: 'relevance',
  sortOrder: 'desc',
};

type ArrayFilterKey = 'categories' | 'fileTypes' | 'tags' | 'uploadedBy';

interface SearchFiltersProps {
  onFiltersChange: (filters: SearchFiltersType) => void;
  filters?: SearchFiltersType;
  facets: SearchFacets;
}

/**
 * Controlled: the page renders two instances (inline and in the mobile sheet),
 * so the selection has to live in the parent — a local copy in either instance
 * desyncs the other.
 */
const SearchFilters: React.FC<SearchFiltersProps> = ({
  onFiltersChange,
  filters = DEFAULT_SEARCH_FILTERS,
  facets,
}) => {
  const { t } = useTranslation();

  const update = (partial: Partial<SearchFiltersType>) =>
    onFiltersChange({ ...filters, ...partial });

  const toggle = (key: ArrayFilterKey, value: string, checked: boolean) =>
    update({
      [key]: checked ? [...filters[key], value] : filters[key].filter((item) => item !== value),
    } as Partial<SearchFiltersType>);

  const activeCount =
    filters.categories.length +
    filters.fileTypes.length +
    filters.tags.length +
    filters.uploadedBy.length +
    (filters.dateRange.start || filters.dateRange.end ? 1 : 0) +
    (filters.searchType !== 'basic' ? 1 : 0);

  const sections: Array<{ id: ArrayFilterKey; label: string; format?: (v: string) => string }> = [
    { id: 'categories', label: t('documents.categories') },
    { id: 'fileTypes', label: t('documents.fileTypes'), format: (value) => value.toUpperCase() },
    { id: 'tags', label: t('documents.tags') },
    { id: 'uploadedBy', label: t('documents.uploadedBy') },
  ];

  return (
    <aside className="rounded-xl border bg-card p-4">
      <div className="flex items-center justify-between gap-2 pb-2">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          <SlidersHorizontal className="size-4 text-muted-foreground" strokeWidth={1.9} />
          {t('search.filters')}
          {activeCount > 0 && (
            <Badge variant="secondary" className="revy-figure font-normal">
              {activeCount}
            </Badge>
          )}
        </h2>

        {activeCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="-mr-2 h-8 text-muted-foreground"
            onClick={() => update(DEFAULT_SEARCH_FILTERS)}
          >
            <X className="size-3.5" strokeWidth={2} />
            {t('common.clearAll')}
          </Button>
        )}
      </div>

      <Accordion type="multiple" defaultValue={['searchType', 'categories']}>
        <AccordionItem value="searchType">
          <AccordionTrigger className="text-sm">{t('search.searchType')}</AccordionTrigger>
          <AccordionContent>
            <Select
              value={filters.searchType}
              onValueChange={(value) =>
                update({ searchType: value as SearchFiltersType['searchType'] })
              }
            >
              <SelectTrigger aria-label={t('search.searchType')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="revy-ui">
                {SEARCH_TYPES.map(({ value, labelKey, icon: Icon }) => (
                  <SelectItem key={value} value={value}>
                    <span className="flex items-center gap-2">
                      <Icon className="size-4" strokeWidth={1.9} />
                      {t(labelKey)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="sort">
          <AccordionTrigger className="text-sm">{t('search.sortOptions')}</AccordionTrigger>
          <AccordionContent className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">{t('search.sortBy')}</Label>
              <Select
                value={filters.sortBy}
                onValueChange={(value) => update({ sortBy: value as SearchFiltersType['sortBy'] })}
              >
                <SelectTrigger aria-label={t('search.sortBy')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="revy-ui">
                  {SEARCH_SORT_OPTIONS.map(({ value, labelKey }) => (
                    <SelectItem key={value} value={value}>
                      {t(labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">{t('search.order')}</Label>
              <Select
                value={filters.sortOrder}
                onValueChange={(value) => update({ sortOrder: value as 'asc' | 'desc' })}
              >
                <SelectTrigger aria-label={t('search.order')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="revy-ui">
                  <SelectItem value="desc">{t('search.sortOrder.desc')}</SelectItem>
                  <SelectItem value="asc">{t('search.sortOrder.asc')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </AccordionContent>
        </AccordionItem>

        {sections.map(({ id, label, format }) => (
          <AccordionItem key={id} value={id}>
            <AccordionTrigger className="text-sm">
              <span className="flex items-center gap-2">
                {label}
                {filters[id].length > 0 && (
                  <Badge variant="secondary" className="revy-figure font-normal">
                    {filters[id].length}
                  </Badge>
                )}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <FilterCheckboxGroup
                options={facets[id]}
                selected={filters[id]}
                emptyLabel={t('search.noFacetOptions')}
                formatLabel={format}
                onToggle={(value, checked) => toggle(id, value, checked)}
              />
            </AccordionContent>
          </AccordionItem>
        ))}

        <AccordionItem value="dateRange">
          <AccordionTrigger className="text-sm">
            <span className="flex items-center gap-2">
              {t('documents.dateRange')}
              {(filters.dateRange.start || filters.dateRange.end) && (
                <Badge variant="secondary" className="font-normal">
                  {t('search.active')}
                </Badge>
              )}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <DateRangePicker
              // One control, so a range cannot be entered backwards and the
              // calendar can show the span it covers.
              value={
                filters.dateRange.start || filters.dateRange.end
                  ? {
                      from: filters.dateRange.start ?? undefined,
                      to: filters.dateRange.end ?? undefined,
                    }
                  : undefined
              }
              onChange={(range) =>
                update({ dateRange: { start: range?.from ?? null, end: range?.to ?? null } })
              }
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </aside>
  );
};

export default SearchFilters;
